import {
  Component,
  ChangeDetectionStrategy,
  input,
  ViewChild,
  AfterViewInit,
  ElementRef,
  computed,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent, TooltipDirective } from '@ui';
import { NzTreeModule } from 'ng-zorro-antd/tree';
import { NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import { TranslocoDirective } from '@jsverse/transloco';

export interface EventsPopoverData {
  centerId: string;
  centerName?: string;
  totalEvents: number;
  trafos: Array<{
    name: string;
    rsabtEvent: number;
    trafoEvents: number;
    vtnEvent: number;
    rsabtId?: string;
    vtnId?: string;
    cuadros: Array<{
      Id: string;
      name: string;
      lines: Array<{
        id: string;
        name: string;
        events: number;
      }>;
    }>;
  }>;
}

export interface AlarmsPopoverData {
  centerId: string;
  centerName?: string;
  totalAlerts: number;
  trafos: Array<{
    name: string;
    rsabtAlert: number;
    trafoAlerts: number;
    vtnAlert: number;
    rsabtId?: string;
    vtnId?: string;
    cuadros: Array<{
      Id: string;
      name: string;
      lines: Array<{
        id: string;
        name: string;
        alerts: number;
      }>;
    }>;
  }>;
}

// For future use when we need both events and alarms
export interface BothPopoverData {
  centerId: string;
  centerName?: string;
  totalEvents: number;
  totalAlerts: number;
  trafos: Array<{
    name: string;
    rsabtEvent: number;
    trafoEvents: number;
    vtnEvent: number;
    rsabtAlert: number;
    trafoAlerts: number;
    vtnAlert: number;
    rsabtId?: string;
    vtnId?: string;
    cuadros: Array<{
      Id: string;
      name: string;
      lines: Array<{
        id: string;
        name: string;
        events: number;
        alerts: number;
      }>;
    }>;
  }>;
}

// Legacy interface for backward compatibility - will be removed
export interface PopoverData {
  centerId: string;
  centerName?: string;
  totalEvents?: number;
  totalAlerts?: number; // Changed from totalAlarms to match API
  type?: 'events' | 'alarms'; // Discriminator to handle different data types
  trafos?: Array<{
    name: string;
    rsabtEvent?: number;
    trafoEvents?: number;
    vtnEvent?: number;
    rsabtAlert?: number; // Changed from rsabtAlarm to match API
    trafoAlerts?: number; // Changed from trafoAlarms to match API
    vtnAlert?: number; // Changed from vtnAlarm to match API
    rsabtId?: string;
    vtnId?: string;
    cuadros: Array<{
      Id: string;
      name: string;
      lines: Array<{
        id: string;
        name: string;
        events?: number;
        alerts?: number; // Changed from alarms to match API
      }>;
    }>;
  }>;
}

@Component({
  selector: 'ui-map-popover',
  standalone: true,
  imports: [CommonModule, IconComponent, NzTreeModule, TranslocoDirective, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *transloco="let t">
      <div #popoverContent class="p-0 min-w-[400px] font-sans overflow-hidden">
      <!-- Header with center name and icons -->
      <div class="flex justify-between items-center px-4 py-2 border-b border-gray-200">
        <h3 class="m-0 text-base font-medium text-black">{{ currentData()?.centerName || 'Centro' }}</h3>
        <div class="flex gap-2">
          <!-- Always show line-chart icon -->
          <div class="icon-wrapper cursor-pointer" (click)="onLineChartIconClick()">
            <ui-icon
              uiTooltip
              [tooltipTitle]="toolTipTemplate"
              icon="line-chart"
              [class]="'text-[14px] text-black'" />
          </div>
          <ng-template #toolTipTemplate>
            {{ t('ACTIONS.CENTER') }}
          </ng-template>

          <!-- Show notification icon for events and both -->
          @if (currentDataType() === 'events' || currentDataType() === 'both') {
            <div class="icon-wrapper cursor-pointer">
              <ui-icon
                uiTooltip
                [tooltipTitle]="toolTipTemplate"
                icon="notification"
                [class]="'text-[14px] text-black'"
                (click)="onNotificationIconClick()" />
            </div>
            <ng-template #toolTipTemplate>
              {{ t('ACTIONS.EVENTS') }}
            </ng-template>
          }

          <!-- Show bell icon for alarms and both -->
          @if (currentDataType() === 'alarms' || currentDataType() === 'both') {
            <div class="icon-wrapper cursor-pointer">
              <ui-icon
                uiTooltip
                [tooltipTitle]="toolTipTemplate"
                icon="bell"
                [class]="'text-[14px] text-black'"
                (click)="onBellIconClick()" />
            </div>
            <ng-template #toolTipTemplate>
              {{ t('ACTIONS.ALARMS') }}
            </ng-template>
          }
        </div>
      </div>

      <!-- Table header for events only -->
      @if (currentDataType() === 'events') {
        <div class="grid grid-cols-[1fr_60px] px-4 py-2 text-[10px] font-medium text-black border-b border-gray-200">
          <div class="text-left"></div>
          <div class="text-right">Eventos</div>
        </div>
      }

      <!-- Table header for alarms only -->
      @if (currentDataType() === 'alarms') {
        <div class="grid grid-cols-[1fr_60px] px-4 py-2 text-[10px] font-medium text-black border-b border-gray-200">
          <div class="text-left"></div>
          <div class="text-right">Alarmas</div>
        </div>
      }

      <!-- Table header for both (future) -->
      @if (currentDataType() === 'both') {
        <div class="grid grid-cols-[1fr_60px_60px] px-4 py-2 text-[10px] font-medium text-black border-b border-gray-200">
          <div class="text-left"></div>
          <div class="text-right">Alarmas</div>
          <div class="text-right">Eventos</div>
        </div>
      }

      <!-- Tree structure for hierarchical data -->
      <div class="px-2 py-2 max-h-[200px] overflow-y-auto">
        @if (treeNodes().length > 0) {
          <!-- For events and alarms (single column) -->
          @if (currentDataType() === 'events' || currentDataType() === 'alarms') {
            <div class="grid grid-cols-[1fr_60px]">
              <!-- Tree column -->
              <div class="pr-2">
                <nz-tree
                  [nzData]="treeNodes()"
                  [nzShowIcon]="false"
                  [nzShowLine]="false"
                  [nzSelectedKeys]="[]"
                  [nzMultiple]="false"
                  nzSize="small">
                  <ng-template #nzTreeTemplate let-node let-origin="origin">
                    <div class="flex items-center gap-[10px]">
                      @if (isLineNode(node)) {
                        <ui-icon
                          icon="icons:line-close"
                          [class]="'text-[12px] text-inherit'" />
                      }
                      <span class="tree-node-title"
                            [class]="(isLineNode(node) || isTrafoNode(node)) ? 'line-node-link' : ''"
                            [title]="node.title"
                            (click)="(isLineNode(node) || isTrafoNode(node)) ? onLineClick(node) : null">
                        {{ isLineNode(node) ? truncateText(node.title, 20) : node.title }}
                      </span>
                    </div>
                  </ng-template>
                </nz-tree>
              </div>

              <!-- Count column -->
              <div class="flex flex-col">
                @for (node of getFlattenedNodes(); track node.key) {
                  <div class="flex justify-end items-center pr-2"
                       [style.height.px]="getNodeHeight(node)"
                       [style.padding-top.px]="getNodePaddingTop(node)"
                       [style.padding-bottom.px]="getNodePaddingBottom(node)">
                    <span class="tree-node-badge"
                          [class]="getBadgeClass(node)"
                          *ngIf="getEventCount(node) >= 0">
                      {{ getEventCount(node) }}
                    </span>
                  </div>
                }
              </div>
            </div>
          }

          <!-- For both events and alarms (two columns) -->
          @if (currentDataType() === 'both') {
            <div class="grid grid-cols-[1fr_60px_60px]">
              <!-- Tree column -->
              <div class="pr-2">
                <nz-tree
                  [nzData]="treeNodes()"
                  [nzShowIcon]="false"
                  [nzShowLine]="false"
                  [nzSelectedKeys]="[]"
                  [nzMultiple]="false"
                  nzSize="small">
                  <ng-template #nzTreeTemplate let-node let-origin="origin">
                    <div class="flex items-center gap-[10px]">
                      @if (isLineNode(node)) {
                        <ui-icon
                          icon="icons:line-close"
                          [class]="'text-[12px] text-inherit'" />
                      }
                      <span class="tree-node-title"
                            [class]="(isLineNode(node) || isTrafoNode(node)) ? 'line-node-link' : ''"
                            [title]="node.title"
                            (click)="(isLineNode(node) || isTrafoNode(node)) ? onLineClick(node) : null">
                        {{ isLineNode(node) ? truncateText(node.title, 20) : node.title }}
                      </span>
                    </div>
                  </ng-template>
                </nz-tree>
              </div>

              <!-- Alarms column -->
              <div class="flex flex-col">
                @for (node of getFlattenedNodes(); track node.key) {
                  <div class="flex justify-end items-center pr-2"
                       [style.height.px]="getNodeHeight(node)"
                       [style.padding-top.px]="getNodePaddingTop(node)"
                       [style.padding-bottom.px]="getNodePaddingBottom(node)">
                    <span class="tree-node-badge"
                          [class]="getAlarmBadgeClass(node)"
                          *ngIf="getAlarmCount(node) >= 0">
                      {{ getAlarmCount(node) }}
                    </span>
                  </div>
                }
              </div>

              <!-- Events column -->
              <div class="flex flex-col">
                @for (node of getFlattenedNodes(); track node.key) {
                  <div class="flex justify-end items-center pr-2"
                       [style.height.px]="getNodeHeight(node)"
                       [style.padding-top.px]="getNodePaddingTop(node)"
                       [style.padding-bottom.px]="getNodePaddingBottom(node)">
                    <span class="tree-node-badge"
                          [class]="getEventBadgeClass(node)"
                          *ngIf="getEventCount(node) >= 0">
                      {{ getEventCount(node) }}
                    </span>
                  </div>
                }
              </div>
            </div>
          }
        } @else {
          <div class="px-4 py-3 text-center text-gray-500 text-sm">
            @if (loading()) {
              {{ t('MAP_POPOVER.LOADING') }}
            } @else {
              {{ t('MAP_POPOVER.NO_DATA') }}
            }
          </div>
        }
      </div>
    </div>
    </ng-container>
  `,
  styles: [
    `
      /* Estilos específicos que no se pueden hacer fácilmente con Tailwind */
      :host {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        --primary-link-color: #1890FF;
      }

      /* Custom tree node styles */
      .tree-node-title {
        font-size: 12px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Line node link styles */
      .line-node-link {
        color: var(--primary-link-color);
        cursor: pointer;
      }

      .line-node-link:hover {
        text-decoration: underline;
      }

      /* Override ant design tree styles */
      ::ng-deep .ant-tree .ant-tree-node-content-wrapper {
        padding: 2px 4px;
        border-radius: 4px;
      }

      ::ng-deep .ant-tree .ant-tree-node-content-wrapper:hover {
        background-color: #f5f5f5;
      }

      ::ng-deep .ant-tree .ant-tree-title {
        font-size: 12px;
      }

      ::ng-deep .ant-tree .ant-tree-switcher {
        width: 16px;
        height: 16px;
        line-height: 16px;
      }

      /* Add proper spacing for the tree structure */
      ::ng-deep .ant-tree .ant-tree-treenode {
        padding: 1px 0;
      }

      /* Style the tree indentation */
      ::ng-deep .ant-tree .ant-tree-indent-unit {
        width: 20px;
      }

      /* Make switcher icons smaller and more subtle */
      ::ng-deep .ant-tree .ant-tree-switcher-icon {
        font-size: 10px;
        color: #999;
      }

      /* Ensure icons are clickable */
      .icon-wrapper {
        display: inline-block;
        padding: 2px;
        border-radius: 4px;
      }
    `,
  ],
})
export class MapPopoverComponent implements AfterViewInit {
  @ViewChild('popoverContent', { static: true }) popoverContent!: ElementRef;

  // Separate inputs for each data type
  eventsData = input<EventsPopoverData | null>(null);
  alarmsData = input<AlarmsPopoverData | null>(null);
  bothData = input<BothPopoverData | null>(null);

  // Legacy input for backward compatibility - will be removed
  data = input<PopoverData>();

  // Distributor ID input - should be passed from marker data
  distributorId = input<string>();

  // Loading state input
  loading = input<boolean>(false);

  // Outputs
  notificationIconClick = output<any>();
  bellIconClick = output<any>();
  lineChartIconClick = output<any>();
  lineClick = output<{ distributorId: string; centerId: string; rsabtId: string; lineId: string; vtnId?: string; url?: string }>();

  // Computed property to determine which type of data we have
  currentDataType = computed(() => {
    if (this.eventsData()) return 'events';
    if (this.alarmsData()) return 'alarms';
    if (this.bothData()) return 'both';
    if (this.data()) return this.data()?.type || 'events';
    return null;
  });

  // Computed property to get the current data regardless of type
  currentData = computed(() => {
    const eventsData = this.eventsData();
    const alarmsData = this.alarmsData();
    const bothData = this.bothData();
    const legacyData = this.data();

    return eventsData || alarmsData || bothData || legacyData || null;
  });

  // Computed property to convert data to tree format - everything expanded
  treeNodes = computed(() => {
    const popoverData = this.currentData();
    if (!popoverData || !popoverData.trafos || popoverData.trafos.length === 0) {
      return [];
    }

    return popoverData.trafos.map((trafo, trafoIndex) => {
      const trafoKey = `trafo-${trafoIndex}`;

      const trafoNode: NzTreeNodeOptions = {
        title: trafo.name,
        key: trafoKey,
        expanded: true, // Always expanded
        isLeaf: !trafo.cuadros || trafo.cuadros.length === 0,
        children: []
      };

      // Add cuadros as children (only if cuadros exists and is not null)
      if (trafo.cuadros && Array.isArray(trafo.cuadros)) {
        trafo.cuadros.forEach((cuadro, cuadroIndex) => {
          const cuadroKey = `${trafoKey}-cuadro-${cuadroIndex}`;

          const cuadroNode: NzTreeNodeOptions = {
            title: cuadro.name,
            key: cuadroKey,
            expanded: true, // Always expanded
            isLeaf: !cuadro.lines || cuadro.lines.length === 0,
            children: []
          };

          // Add lines as children of cuadros (only if lines exists and is not null)
          if (cuadro.lines && Array.isArray(cuadro.lines)) {
            cuadro.lines.forEach((line, lineIndex) => {
              const lineKey = `${cuadroKey}-line-${lineIndex}`;
              const lineNode: NzTreeNodeOptions = {
                title: line.name,
                key: lineKey,
                isLeaf: true
              };

              cuadroNode.children!.push(lineNode);
            });
          }

          trafoNode.children!.push(cuadroNode);
        });
      }

      return trafoNode;
    });
  });  // Get flattened nodes for the events column
  getFlattenedNodes(): any[] {
    const nodes: any[] = [];

    const flattenNode = (node: NzTreeNodeOptions) => {
      nodes.push(node);
      if (node.children && node.expanded !== false) {
        node.children.forEach(child => flattenNode(child));
      }
    };

    this.treeNodes().forEach(node => flattenNode(node));
    return nodes;
  }

  // Get node height for alignment
  getNodeHeight(node: any): number {
    return 30; // Match ant design tree node height exactly
  }

  // Get node padding top for alignment
  getNodePaddingTop(node: any): number {
    return 2; // Match tree node padding
  }

  // Get node padding bottom for alignment
  getNodePaddingBottom(node: any): number {
    return 2; // Match tree node padding
  }

  ngAfterViewInit() {
    // Este método permite obtener el HTML del contenido para Leaflet
  }

  getHtmlContent(): string {
    return this.popoverContent.nativeElement.outerHTML;
  }

  // Helper method to get the correct label based on data type
  getCountLabel(): string {
    const dataType = this.currentDataType();
    return dataType === 'alarms' ? 'Alarmas' : 'Eventos';
  }

  // Helper method to get the count value from line based on type
  private getLineCount(line: any): number {
    const dataType = this.currentDataType();
    if (dataType === 'alarms') {
      return line.alerts ?? line.totalAlerts ?? 0;
    }
    return line.events ?? line.totalEvents ?? 0;
  }

  // Helper method to get trafo counts based on type
  private getTrafoCount(trafo: any): number {
    const dataType = this.currentDataType();
    if (dataType === 'alarms') {
      return (trafo.rsabtAlert ?? 0) + (trafo.trafoAlerts ?? trafo.totalAlerts ?? 0) + (trafo.vtnAlert ?? 0);
    }
    return (trafo.rsabtEvent ?? 0) + (trafo.trafoEvents ?? trafo.totalEvents ?? 0) + (trafo.vtnEvent ?? 0);
  }

  // Calcula el total de eventos/alarmas para el centro
  getTotalCount(): number {
    const data = this.currentData();
    if (!data || !data.trafos) return 0;

    return data.trafos.reduce((total, trafo) => {
      const trafoCount = this.getTrafoCount(trafo);
      const cuadrosCount = trafo.cuadros?.reduce((cuadroSum, cuadro) => {
        return cuadroSum + (cuadro.lines?.reduce((lineSum, line) => lineSum + this.getLineCount(line), 0) ?? 0);
      }, 0) ?? 0;
      return total + trafoCount + cuadrosCount;
    }, 0);
  }

  // Get event count for a tree node
  getEventCount(node: any): number {
    const key = node.key as string;
    const data = this.currentData();
    const dataType = this.currentDataType();
    if (!data || !data.trafos) return 0;

    // Check for line nodes first (most specific)
    if (key.includes('-line-')) {
      // Parse the key correctly: trafo-X-cuadro-Y-line-Z
      const parts = key.split('-');
      if (parts.length >= 6) {
        const trafoIndex = parseInt(parts[1]);
        const cuadroIndex = parseInt(parts[3]);
        const lineIndex = parseInt(parts[5]);
        const line = data.trafos?.[trafoIndex]?.cuadros?.[cuadroIndex]?.lines?.[lineIndex];
        // For line nodes, show the events/alarms property based on type
        return line ? this.getLineCount(line) : 0;
      }
    }

    // Check for cuadro nodes second
    if (key.includes('-cuadro-')) {
      const parts = key.split('-cuadro-');
      const trafoIndex = parseInt(parts[0].split('-')[1]);
      const cuadroIndex = parseInt(parts[1]);
      const cuadro = data.trafos?.[trafoIndex]?.cuadros?.[cuadroIndex];
      if (!cuadro) return 0;

      // For cuadro nodes, show sum of all line events/alarms
      const sumCount = cuadro.lines?.reduce((sum, line) => sum + this.getLineCount(line), 0) ?? 0;
      return sumCount;
    }

    // Check for trafo nodes last (least specific)
    if (key.startsWith('trafo-')) {
      const trafoIndex = parseInt(key.split('-')[1]);
      const trafo = data.trafos?.[trafoIndex];
      if (!trafo) return 0;

      // For trafo nodes, use the appropriate trafo count based on type
      if (dataType === 'alarms') {
        return (trafo as any).trafoAlerts ?? (trafo as any).totalAlerts ?? 0;
      }
      return (trafo as any).trafoEvents ?? (trafo as any).totalEvents ?? 0;
    }

    return 0;
  }

  // Get alarm count for a tree node (used when showing both events and alarms)
  getAlarmCount(node: any): number {
    const key = node.key as string;
    const data = this.currentData();
    if (!data || !data.trafos || this.currentDataType() !== 'both') return 0;

    // Check for line nodes first (most specific)
    if (key.includes('-line-')) {
      // Parse the key correctly: trafo-X-cuadro-Y-line-Z
      const parts = key.split('-');
      if (parts.length >= 6) {
        const trafoIndex = parseInt(parts[1]);
        const cuadroIndex = parseInt(parts[3]);
        const lineIndex = parseInt(parts[5]);
        const line = data.trafos?.[trafoIndex]?.cuadros?.[cuadroIndex]?.lines?.[lineIndex];
        return line ? ((line as any).alerts ?? (line as any).totalAlerts ?? 0) : 0;
      }
    }

    // Check for cuadro nodes second
    if (key.includes('-cuadro-')) {
      const parts = key.split('-cuadro-');
      const trafoIndex = parseInt(parts[0].split('-')[1]);
      const cuadroIndex = parseInt(parts[1]);
      const cuadro = data.trafos?.[trafoIndex]?.cuadros?.[cuadroIndex];
      if (!cuadro) return 0;

      // For cuadro nodes, show sum of all line alerts
      const sumCount = cuadro.lines?.reduce((sum, line) => sum + ((line as any).alerts ?? (line as any).totalAlerts ?? 0), 0) ?? 0;
      return sumCount;
    }

    // Check for trafo nodes last (least specific)
    if (key.startsWith('trafo-')) {
      const trafoIndex = parseInt(key.split('-')[1]);
      const trafo = data.trafos?.[trafoIndex];
      if (!trafo) return 0;

      // For trafo nodes, use the alarm count
      return (trafo as any).trafoAlerts ?? (trafo as any).totalAlerts ?? 0;
    }

    return 0;
  }

  // Get badge class based on node type
  getBadgeClass(node: any): string {
    const key = node.key as string;
    const dataType = this.currentDataType();

    if (dataType === 'alarms') {
      // Alarms specific colors
      if (key.includes('-line-')) {
        return 'alarm-badge-line';
      } else if (key.includes('-cuadro-')) {
        return 'alarm-badge-cuadro';
      } else if (key.startsWith('trafo-')) {
        return 'alarm-badge-trafo';
      }
    }

    // Default colors for events or fallback
    if (key.includes('-line-')) {
      return 'badge-line';
    } else if (key.includes('-cuadro-')) {
      return 'badge-cuadro';
    } else if (key.startsWith('trafo-')) {
      return 'badge-trafo';
    }

    return '';
  }

  // Get alarm badge class for both mode
  getAlarmBadgeClass(node: any): string {
    const key = node.key as string;

    if (key.includes('-line-')) {
      return 'alarm-badge-line';
    } else if (key.includes('-cuadro-')) {
      return 'alarm-badge-cuadro';
    } else if (key.startsWith('trafo-')) {
      return 'alarm-badge-trafo';
    }

    return '';
  }

  // Get event badge class for both mode
  getEventBadgeClass(node: any): string {
    const key = node.key as string;

    if (key.includes('-line-')) {
      return 'badge-line';
    } else if (key.includes('-cuadro-')) {
      return 'badge-cuadro';
    } else if (key.startsWith('trafo-')) {
      return 'badge-trafo';
    }

    return '';
  }

  // Check if node is a line node
  isLineNode(node: any): boolean {
    const key = node.key as string;
    return key.includes('-line-');
  }

  // Check if node is a trafo node (but not a cuadro or line)
  isTrafoNode(node: any): boolean {
    const key = node.key as string;
    return key.startsWith('trafo-') && !key.includes('-cuadro-') && !key.includes('-line-');
  }

  // Truncate text if it's longer than maxLength
  truncateText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }

  // Handle notification icon click
  onNotificationIconClick() {
    this.notificationIconClick.emit(this.currentData());
  }

  // Handle bell icon click
  onBellIconClick() {
    this.bellIconClick.emit(this.currentData());
  }

  // Handle line chart icon click
  onLineChartIconClick() {
    const data = this.currentData();
    const distributorId = this.distributorId();

    if (!data || !distributorId) {
      console.warn('Missing data or distributorId', { data, distributorId });
      return;
    }

    // Find the first trafo with rsabtId for navigation
    const trafoWithData = data.trafos?.find(trafo =>
      (trafo as any).rsabtId
    );

    if (trafoWithData) {
      const rsabtId = (trafoWithData as any).rsabtId;
      const vtnId = (trafoWithData as any).vtnId;

      // Build the base navigation URL
      const baseUrl = `/distributors/${distributorId}/center/${data.centerId}/rsabts/${rsabtId}`;

      // Add vtnId as query parameter only if it exists and is not empty
      const url = (vtnId && vtnId.trim() !== '') ? `${baseUrl}?vtnId=${vtnId}` : baseUrl;

      this.lineChartIconClick.emit({
        distributorId,
        centerId: data.centerId,
        rsabtId,
        vtnId: vtnId || '',
        url
      });
    } else {
      console.warn('Navigation skipped: no trafo found with rsabtId');
    }
  }

  // Handle line click for navigation
  onLineClick(node: any) {
    const key = node.key as string;
    const data = this.currentData();
    const distributorId = this.distributorId();

    if (!data || !data.trafos || !distributorId) return;

    // Check if it's a trafo node (transformer)
    if (key.startsWith('trafo-') && !key.includes('-cuadro-')) {
      const trafoIndex = parseInt(key.split('-')[1]);
      const trafo = data.trafos[trafoIndex];

      if (trafo) {
        const rsabtId = (trafo as any).rsabtId;
        const vtnId = (trafo as any).vtnId;

        if (rsabtId) {
          // Build the navigation URL for trafo
          const baseUrl = `/distributors/${distributorId}/center/${data.centerId}/rsabts/${rsabtId}`;

          // Add vtnId as query parameter if it exists and is not empty
          const url = (vtnId && vtnId.trim() !== '') ? `${baseUrl}?vtnId=${vtnId}` : baseUrl;

          this.lineClick.emit({
            distributorId,
            centerId: data.centerId,
            rsabtId,
            lineId: '', // Empty for trafo navigation
            vtnId: vtnId || '',
            url // Pass the complete URL
          });
        } else {
          console.warn('Navigation skipped: missing rsabtId for trafo', { rsabtId });
        }
      }
      return;
    }

    // Parse the key for line navigation: trafo-X-cuadro-Y-line-Z
    const parts = key.split('-');
    if (parts.length >= 6) {
      const trafoIndex = parseInt(parts[1]);
      const cuadroIndex = parseInt(parts[3]);
      const lineIndex = parseInt(parts[5]);

      const trafo = data.trafos[trafoIndex];
      const line = trafo?.cuadros?.[cuadroIndex]?.lines?.[lineIndex];

      if (trafo && line) {
        // Get navigation data for line
        const rsabtId = (trafo as any).rsabtId;
        const lineId = line.id;

        if (rsabtId && lineId) {
          // Emit the line click event with navigation data
          this.lineClick.emit({
            distributorId,
            centerId: data.centerId,
            rsabtId,
            lineId,
            vtnId: '',
            url: `/distributors/${distributorId}/center/${data.centerId}/rsabts/${rsabtId}/tsabts/${lineId}`
          });
        } else {
          console.warn('Navigation skipped: missing rsabtId or lineId', { rsabtId, lineId });
        }
      }
    }
  }
}
